// Тонкий клиент к /api. Куки сессии same-origin отправляются автоматически.
// Ошибки сервера приходят как { ok: false, error }, статус — в HTTP-коде.

import type {
  Actor,
  Attachment,
  BoardColumn,
  BoardColumnColor,
  Comment,
  GenerationColumn,
  GenerationSource,
  GeneratedPost,
  GeneratedPostFeedback,
  GeneratedPostStatus,
  Keyword,
  KeywordSearchOptions,
  Pagination,
  Post,
  PostListFilters,
  Project,
  ProjectInvitation,
  ProjectMember,
  ReplyTemplate,
  SearchRun
} from './types'

export interface GeneratedTemplate {
  title: string
  body: string
}

export class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Для FormData Content-Type ставит браузер (с boundary) — сами не трогаем.
  const isFormData = init?.body instanceof FormData
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers
    }
  })

  if (response.status === 204) {
    return undefined as T
  }

  const data = (await response.json().catch(() => null)) as unknown
  if (!response.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `Ошибка ${response.status}`
    throw new ApiError(response.status, message)
  }
  return data as T
}

function body(value: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(value) }
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value))
    }
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export const api = {
  // --- Сессия / авторизация ---
  me: () => request<{ actor: Actor | null }>('/api/me'),
  updateProfile: (input: { emailNotifications?: boolean }) =>
    request<{ actor: Actor }>('/api/me', { method: 'PATCH', body: JSON.stringify(input) }),
  requestCode: (email: string) => request<{ ok: boolean; cooldownSeconds?: number }>('/api/auth/request-code', body({ email })),
  verifyCode: (email: string, code: string) => request<{ ok: boolean }>('/api/auth/verify-code', body({ email, code })),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  // --- Проекты ---
  listProjects: () => request<{ projects: Project[] }>('/api/projects'),
  listProjectInvitations: () => request<{ invitations: ProjectInvitation[] }>('/api/project-invitations'),
  createProject: (input: { name: string; description?: string }) => request<{ project: Project }>('/api/projects', body(input)),
  getProject: (projectId: string) => request<{ project: Project }>(`/api/projects/${projectId}`),
  updateProject: (
    projectId: string,
    input: { name?: string; description?: string; searchMaxAgeDays?: number; replyExamples?: string[] }
  ) => request<{ project: Project }>(`/api/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteProject: (projectId: string) => request<void>(`/api/projects/${projectId}`, { method: 'DELETE' }),
  listBoardColumns: (projectId: string) => request<{ columns: BoardColumn[] }>(`/api/projects/${projectId}/columns`),
  createBoardColumn: (projectId: string, input: { name: string; color?: BoardColumnColor }) =>
    request<{ column: BoardColumn }>(`/api/projects/${projectId}/columns`, body(input)),
  updateBoardColumn: (columnId: string, input: { name?: string; color?: BoardColumnColor }) =>
    request<{ column: BoardColumn }>(`/api/columns/${columnId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteBoardColumn: (columnId: string, input: { moveToColumnId?: string }) =>
    request<void>(`/api/columns/${columnId}`, { method: 'DELETE', body: JSON.stringify(input) }),
  reorderBoardColumns: (projectId: string, columnIds: string[]) =>
    request<{ columns: BoardColumn[] }>(`/api/projects/${projectId}/columns/reorder`, body({ columnIds })),
  listGenerationSources: (projectId: string) =>
    request<{ sources: GenerationSource[] }>(`/api/projects/${projectId}/generation-sources`),
  createGenerationSource: (
    projectId: string,
    input: { type: GenerationSource['type']; title?: string; url?: string; contentText?: string }
  ) => request<{ source: GenerationSource }>(`/api/projects/${projectId}/generation-sources`, body(input)),
  updateGenerationSource: (sourceId: string, input: { title?: string; contentText?: string }) =>
    request<{ source: GenerationSource }>(`/api/generation-sources/${sourceId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteGenerationSource: (sourceId: string) => request<void>(`/api/generation-sources/${sourceId}`, { method: 'DELETE' }),
  listGenerationColumns: (projectId: string) =>
    request<{ columns: GenerationColumn[] }>(`/api/projects/${projectId}/generation-columns`),
  createGenerationColumn: (projectId: string, input: { name: string; color?: BoardColumnColor }) =>
    request<{ column: GenerationColumn }>(`/api/projects/${projectId}/generation-columns`, body(input)),
  updateGenerationColumn: (columnId: string, input: { name?: string; color?: BoardColumnColor }) =>
    request<{ column: GenerationColumn }>(`/api/generation-columns/${columnId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteGenerationColumn: (columnId: string, input: { moveToColumnId?: string }) =>
    request<void>(`/api/generation-columns/${columnId}`, { method: 'DELETE', body: JSON.stringify(input) }),
  reorderGenerationColumns: (projectId: string, columnIds: string[]) =>
    request<{ columns: GenerationColumn[] }>(`/api/projects/${projectId}/generation-columns/reorder`, body({ columnIds })),
  updateProjectNotifications: (projectId: string, emailNotifications: boolean) =>
    request<{ project: Project }>(`/api/projects/${projectId}/notifications`, {
      method: 'PATCH',
      body: JSON.stringify({ emailNotifications })
    }),
  listProjectMembers: (projectId: string) =>
    request<{ members: ProjectMember[]; canManageMembers: boolean }>(`/api/projects/${projectId}/members`),
  inviteProjectMember: (projectId: string, email: string) =>
    request<{ member: ProjectMember | null }>(`/api/projects/${projectId}/members`, body({ email })),
  removeProjectMember: (projectId: string, email: string) =>
    request<void>(`/api/projects/${projectId}/members/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  acceptProjectInvite: (projectId: string) =>
    request<{ project: Project }>(`/api/projects/${projectId}/invitations/accept`, { method: 'POST' }),
  declineProjectInvite: (projectId: string) =>
    request<void>(`/api/projects/${projectId}/invitations/decline`, { method: 'POST' }),

  // --- Ключевые слова ---
  listKeywords: (projectId: string) => request<{ keywords: Keyword[] }>(`/api/projects/${projectId}/keywords`),
  createKeyword: (
    projectId: string,
    input: { query: string; isActive?: boolean; autoSearchIntervalMinutes?: number; searchOptions?: KeywordSearchOptions }
  ) => request<{ keyword: Keyword }>(`/api/projects/${projectId}/keywords`, body(input)),
  updateKeyword: (
    keywordId: string,
    input: { query?: string; isActive?: boolean; autoSearchIntervalMinutes?: number; searchOptions?: KeywordSearchOptions }
  ) =>
    request<{ keyword: Keyword }>(`/api/keywords/${keywordId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteKeyword: (keywordId: string) => request<void>(`/api/keywords/${keywordId}`, { method: 'DELETE' }),
  searchKeyword: (keywordId: string) =>
    request<{ run: SearchRun; foundCount: number; newCount: number }>(`/api/keywords/${keywordId}/search`, { method: 'POST' }),

  // --- Посты ---
  listPosts: (projectId: string, filters: PostListFilters = {}) =>
    request<{ posts: Post[]; pagination: Pagination }>(`/api/projects/${projectId}/posts${buildQuery({ ...filters })}`),
  listProjectLanguages: (projectId: string) =>
    request<{ languages: { code: string; count: number }[] }>(`/api/projects/${projectId}/languages`),
  getPost: (postId: string) => request<{ post: Post }>(`/api/posts/${postId}`),
  updatePost: (
    postId: string,
    input: {
      columnId?: string
      assignedToEmail?: string | null
      isFavorite?: boolean
      rejectReason?: string
      draftHtml?: string
    }
  ) => request<{ post: Post }>(`/api/posts/${postId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deletePost: (postId: string) => request<void>(`/api/posts/${postId}`, { method: 'DELETE' }),
  generateDraft: (postId: string) => request<{ post: Post; draftHtml: string }>(`/api/posts/${postId}/draft`, { method: 'POST' }),
  reorderPosts: (projectId: string, columnId: string, orderedIds: string[]) =>
    request<{ ok: boolean; updated: number }>(`/api/projects/${projectId}/posts/reorder`, body({ columnId, orderedIds })),

  // --- Комментарии к посту ---
  listPostComments: (postId: string) => request<{ comments: Comment[] }>(`/api/posts/${postId}/comments`),
  createComment: (postId: string, input: { html: string; attachmentIds?: string[] }) =>
    request<{ comment: Comment }>(`/api/posts/${postId}/comments`, body(input)),
  updateComment: (commentId: string, input: { html: string }) =>
    request<{ comment: Comment }>(`/api/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteComment: (commentId: string) => request<void>(`/api/comments/${commentId}`, { method: 'DELETE' }),

  // --- Вложения комментариев ---
  uploadAttachment: (postId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<{ attachment: Attachment }>(`/api/posts/${postId}/attachments`, { method: 'POST', body: form })
  },
  deleteAttachment: (attachmentId: string) => request<void>(`/api/attachments/${attachmentId}`, { method: 'DELETE' }),

  // --- Шаблоны ответов ---
  listReplyTemplates: (projectId: string) =>
    request<{ templates: ReplyTemplate[] }>(`/api/projects/${projectId}/templates`),
  createReplyTemplates: (
    projectId: string,
    input: { title?: string; body?: string; source?: ReplyTemplate['source']; items?: { title?: string; body: string }[] }
  ) => request<{ templates: ReplyTemplate[] }>(`/api/projects/${projectId}/templates`, body(input)),
  updateReplyTemplate: (templateId: string, input: { title?: string; body?: string }) =>
    request<{ template: ReplyTemplate }>(`/api/templates/${templateId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteReplyTemplate: (templateId: string) => request<void>(`/api/templates/${templateId}`, { method: 'DELETE' }),
  generateReplyTemplates: (projectId: string, input: { count: number; instruction?: string }) =>
    request<{ candidates: GeneratedTemplate[] }>(`/api/projects/${projectId}/templates/generate`, body(input)),

  // --- Генерация постов на залетевших тредах ---
  listGeneratedPosts: (projectId: string, filters: { columnId?: string; limit?: number; offset?: number } = {}) =>
    request<{ generatedPosts: GeneratedPost[]; pagination: Pagination }>(
      `/api/projects/${projectId}/generated-posts${buildQuery({ ...filters })}`
    ),
  generatePosts: (projectId: string, input: { theme?: string; toneNotes?: string; count?: number; sourceIds?: string[] }) =>
    request<{ generatedPosts: GeneratedPost[]; evidenceCount: number }>(
      `/api/projects/${projectId}/generated-posts/generate`,
      body(input)
    ),
  updateGeneratedPost: (
    id: string,
    input: {
      text?: string
      status?: GeneratedPostStatus
      columnId?: string
      feedback?: GeneratedPostFeedback
      publishedUrl?: string
      rejectedReason?: string
    }
  ) =>
    request<{ generatedPost: GeneratedPost }>(`/api/generated-posts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteGeneratedPost: (id: string) => request<void>(`/api/generated-posts/${id}`, { method: 'DELETE' }),
  reorderGeneratedPosts: (projectId: string, columnId: string, orderedIds: string[]) =>
    request<{ ok: boolean; updated: number }>(`/api/projects/${projectId}/generated-posts/reorder`, body({ columnId, orderedIds })),

  // --- Запуски поиска ---
  listSearchRuns: (params: { projectId?: string; keywordId?: string } = {}) =>
    request<{ searchRuns: SearchRun[] }>(`/api/search-runs${buildQuery({ ...params })}`)
}
